import { useEffect } from "react";
import { toast } from "@/components/UIComponents/Toast";
import { ONLINE_ORDERS_ENABLED } from "@/constants/features";
import { useStore } from "@/contexts";
import { onlineOrdersService } from "@/services/onlineOrders";
import { storeNotificationsService } from "@/services/storeNotifications";
import {
  selectEnabledFor,
  useOnlineOrdersStore,
} from "@/store/onlineOrdersStore";

/** How often the badge asks for a fresh count while the app is on screen. */
export const ONLINE_ORDERS_POLL_MS = 60 * 1000;

/**
 * Keeps `useOnlineOrdersStore` in step with the current store's unseen
 * notifications and the orders it still has to ship, and announces the online
 * orders that arrive while the app is open.
 *
 * Mounted once, above the routes, so an order arriving while the store is on
 * any page is announced — not only while the scheduler is on screen.
 *
 * Polling rather than a push channel, because the app has none. It stops while
 * the document is hidden — nobody is there to see a toast, and an embedded
 * Hubs tab left in the background would otherwise poll all day — and catches
 * up the moment it becomes visible again.
 */
export const useOnlineOrdersWatcher = () => {
  const { store, resolvedStoreId } = useStore();
  const { storeCode } = store;
  const enabled = useOnlineOrdersStore(selectEnabledFor(resolvedStoreId));
  const refreshToken = useOnlineOrdersStore(state => state.refreshToken);

  /*
   * Whether this store ships online orders at all. Reset first, so a store
   * switch never shows the previous store's badge while the answer is out.
   */
  useEffect(() => {
    const { reset, setEnabled } = useOnlineOrdersStore.getState();
    reset();
    /* Switched off, the answer stays unknown, so nothing below ever polls. */
    if (!ONLINE_ORDERS_ENABLED || resolvedStoreId === null) return;

    const controller = new AbortController();
    let retry: ReturnType<typeof setTimeout> | undefined;

    const check = () => {
      retry = undefined;

      onlineOrdersService
        .isEnabledForStore(
          { id: resolvedStoreId, storeCode },
          controller.signal,
        )
        .then(isEnabled => {
          if (!controller.signal.aborted) {
            setEnabled(isEnabled, resolvedStoreId);
          }
        })
        .catch(() => {
          /*
           * Only our own signal means "cancelled": HttpClient's timeout also
           * rejects with an AbortError, and that is a failure. The answer
           * stays unknown rather than "no" — the page must not tell a store
           * that ships online orders that it does not — and is asked again,
           * so one bad moment does not hide the feature all day.
           */
          if (controller.signal.aborted) return;
          retry = setTimeout(retryWhileVisible, ONLINE_ORDERS_POLL_MS);
        });
    };

    /*
     * Only the retries wait for someone to be there: the first check runs
     * at once, as the first summary poll does, even in a background tab.
     */
    const retryWhileVisible = () => {
      if (document.visibilityState === "hidden") {
        retry = setTimeout(retryWhileVisible, ONLINE_ORDERS_POLL_MS);
        return;
      }
      check();
    };

    /* A retry waiting out a hidden spell asks as soon as someone is back. */
    const retryWhenShown = () => {
      if (retry === undefined || document.visibilityState !== "visible") {
        return;
      }
      clearTimeout(retry);
      check();
    };

    check();
    document.addEventListener("visibilitychange", retryWhenShown);

    return () => {
      controller.abort();
      clearTimeout(retry);
      document.removeEventListener("visibilitychange", retryWhenShown);
    };
  }, [resolvedStoreId, storeCode]);

  useEffect(() => {
    if (!enabled || resolvedStoreId === null) return;

    let controller: AbortController | null = null;

    const poll = () => {
      controller?.abort();
      const current = new AbortController();
      controller = current;

      storeNotificationsService
        .getUnseenSummary(resolvedStoreId, current.signal)
        .then(summary => {
          if (current.signal.aborted) return;
          if (useOnlineOrdersStore.getState().applySummary(summary)) {
            toast.info("Nova encomenda online recebida.");
          }
        })
        /* A failed poll keeps the last count; the next tick tries again. */
        .catch(() => undefined);

      /* Apart from the summary, so either can fail without the other. */
      onlineOrdersService
        .countToFulfil(resolvedStoreId, current.signal)
        .then(count => {
          if (!current.signal.aborted) {
            useOnlineOrdersStore.getState().setToFulfilCount(count);
          }
        })
        .catch(() => undefined);
    };

    poll();

    const interval = setInterval(() => {
      if (document.visibilityState !== "hidden") poll();
    }, ONLINE_ORDERS_POLL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      controller?.abort();
    };
  }, [enabled, resolvedStoreId, refreshToken]);
};
